import { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { publicidadConfigService } from '../../../services/api';
import InfoTip from './InfoTip';
import styles from './PublicidadTab.module.css';

// Tooltips por campo — un solo lugar para mantenerlos.
const TIPS = {
  formato:       'Dimensiones y tipo del aviso (banner, medium rectangle, etc.). El precio depende del formato porque implica distinta visibilidad y espacio.',
  ubicacion:     'Dónde se muestra el aviso dentro del sitio (home top, sidebar, entre artículos...). Cada ubicación tiene un recargo porcentual asociado.',
  tipoContenido: 'Categoría comercial del aviso (banner display, clasificado, edicto, aviso económico...). Algunos tipos usan tarifas fijas en vez de calculadas.',
  tarifa:        'Tarifa base que se multiplica por las unidades de medida para obtener el precio bruto. Puede quedar vacío si el Tipo de contenido elegido ya tiene una tarifa fija (los clasificados y edictos, por ejemplo, se cobran con precio fijo y no dependen de esta tarifa).',
  unidades:      'Cantidad multiplicadora sobre la tarifa base. Ejemplo: si la tarifa es "por cm × columna", pon 20 para un aviso de 20 cm×col. Para banners digitales normalmente es 1.',
  cromatismo:    'Recargo por uso de color en avisos impresos. Para digital habitualmente no aplica.',
  recargoEd:     'Recargo por edición especial (dominical, lanzamiento, especial temático). Solo aplica en impresos por ahora.',
  modalidad:     'Cómo se contrata la vigencia del aviso: por número de ediciones, por rango de fechas, o indefinida (precio por inserción).',
  numEdiciones:  'Cuántas ediciones aparecerá el aviso. El precio total = precio por inserción × número de ediciones.',
  fechaInicio:   'Primer día en que el aviso estará publicado.',
  fechaFin:      'Último día en que el aviso estará publicado. El total se multiplica por los días del rango (ambos inclusive).',
  agencia:       'Marca esto si el cliente es una agencia de medios acreditada. Aplica el descuento estándar de agencia definido en la configuración, o el porcentaje especial que pongas abajo.',
  descuentoEsp:  'Sobrescribe el descuento estándar de agencia con un valor negociado. Déjalo vacío para usar el estándar del sistema.',
};

const SECCION_STYLE = { fontSize:12, fontWeight:700, color:'#1B4332', margin:'8px 0 6px', textTransform:'uppercase', letterSpacing:0.5 };
const GRID_3COLS   = { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' };

export default function CotizadorTab() {
  const [config, setConfig] = useState({ formatos:[], ubicaciones:[], cromatismo:[], recargosEd:[], tipos:[], tarifas:[] });
  const [form, setForm] = useState({ esAgencia: false, unidadesMedida: 1, modalidadVigencia: 'ediciones', numEdiciones: 1 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      publicidadConfigService.getFormatos(),
      publicidadConfigService.getUbicaciones(),
      publicidadConfigService.getCromatismo(),
      publicidadConfigService.getRecargosEdicion(),
      publicidadConfigService.getTiposContenido(),
      publicidadConfigService.getTarifas(),
    ]).then(([formatos, ubicaciones, cromatismo, recargosEd, tipos, tarifas]) => {
      setConfig({ formatos, ubicaciones, cromatismo, recargosEd, tipos, tarifas });
    });
  }, []);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setResult(null); };

  const cotizar = async () => {
    if (!form.formatoId || !form.ubicacionId || !form.tipoContenidoId) {
      return setError('Selecciona Formato, Ubicación y Tipo de contenido.');
    }
    setError(''); setLoading(true);
    try {
      const dto = {
        formatoId:       Number(form.formatoId),
        ubicacionId:     Number(form.ubicacionId),
        tipoContenidoId: Number(form.tipoContenidoId),
        cromatismoId:    form.cromatismoId    ? Number(form.cromatismoId)    : null,
        recargoEdicionId:form.recargoEdicionId ? Number(form.recargoEdicionId) : null,
        tarifaBaseId:    form.tarifaBaseId    ? Number(form.tarifaBaseId)    : null,
        unidadesMedida:  Number(form.unidadesMedida) || 1,
        esAgencia:       !!form.esAgencia,
        descuentoEspecialPct: form.descuentoEspecialPct ? Number(form.descuentoEspecialPct) : null,
      };
      const r = await publicidadConfigService.cotizar(dto);
      let insercionesTotales = 1;
      if (form.modalidadVigencia === 'ediciones') {
        insercionesTotales = Number(form.numEdiciones) || 1;
      } else if (form.modalidadVigencia === 'fechas' && form.fechaInicio && form.fechaFin) {
        const dias = Math.ceil((new Date(form.fechaFin) - new Date(form.fechaInicio)) / (1000*60*60*24)) + 1;
        insercionesTotales = Math.max(1, dias);
      }
      setResult({ ...r, insercionesTotales, totalConVigencia: r.total * insercionesTotales });
    } catch { setError('Error al calcular. Verifica los parámetros.'); }
    setLoading(false);
  };

  const fmt = (v) => v != null ? `$${Number(v).toLocaleString('es-CO', { minimumFractionDigits: 0 })}` : '-';

  return (
    <div>
      <div className={styles.sectionTitle} style={{ marginBottom:20 }}>
        <Calculator size={16} />
        Cotizador de Pauta
      </div>

      {/* 1. Definición del aviso */}
      <div style={SECCION_STYLE}>1. Definición del aviso</div>
      <div className={styles.form} style={GRID_3COLS}>
        <div className={styles.formGroup}>
          <label>Formato * <InfoTip text={TIPS.formato} /></label>
          <select value={form.formatoId||''} onChange={e => setField('formatoId', e.target.value)}>
            <option value=''>-- Selecciona --</option>
            {config.formatos.map(f => (
              <option key={f.id} value={f.id} disabled={f.canal === 'impreso'}>
                {f.canal === 'impreso' ? '🔒 ' : ''}{f.nombre} ({f.canal}){f.canal === 'impreso' ? ' — próximamente' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Ubicación * <InfoTip text={TIPS.ubicacion} /></label>
          <select value={form.ubicacionId||''} onChange={e => setField('ubicacionId', e.target.value)}>
            <option value=''>-- Selecciona --</option>
            {config.ubicaciones.map(u => (
              <option key={u.id} value={u.id} disabled={u.canal === 'impreso'}>
                {u.canal === 'impreso' ? '🔒 ' : ''}{u.nombre} (+{u.recargoPct}%){u.canal === 'impreso' ? ' — próximamente' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Tipo de contenido * <InfoTip text={TIPS.tipoContenido} /></label>
          <select value={form.tipoContenidoId||''} onChange={e => setField('tipoContenidoId', e.target.value)}>
            <option value=''>-- Selecciona --</option>
            {config.tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}{t.tarifaFijaCop ? ` — Tarifa fija $${t.tarifaFijaCop.toLocaleString('es-CO')}` : ''}</option>)}
          </select>
        </div>
      </div>

      {/* 2. Cálculo del precio */}
      <div style={SECCION_STYLE}>2. Cálculo del precio</div>
      <div className={styles.form} style={GRID_3COLS}>
        <div className={styles.formGroup}>
          <label>Tarifa base <InfoTip text={TIPS.tarifa} /></label>
          <select value={form.tarifaBaseId||''} onChange={e => setField('tarifaBaseId', e.target.value)}>
            <option value=''>-- Sin tarifa --</option>
            {config.tarifas.map(t => {
              const esImpreso = t.nombre?.toLowerCase().includes('impres') || t.nombre?.toLowerCase().includes('módulo') || t.nombre?.toLowerCase().includes('modulo') || t.nombre?.toLowerCase().includes('cm/col');
              return (
                <option key={t.id} value={t.id} disabled={esImpreso}>
                  {esImpreso ? '🔒 ' : ''}{t.nombre} — ${t.valorCop.toLocaleString('es-CO')}/unidad{esImpreso ? ' — próximamente' : ''}
                </option>
              );
            })}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Unidades de medida <InfoTip text={TIPS.unidades} /></label>
          <input type='number' min='0' value={form.unidadesMedida} onChange={e => setField('unidadesMedida', e.target.value)} placeholder='Ej: 20 (cm × col)' />
        </div>
        <div className={styles.formGroup}>
          <label>Cromatismo <InfoTip text={TIPS.cromatismo} /></label>
          <select value={form.cromatismoId||''} onChange={e => setField('cromatismoId', e.target.value)}>
            <option value=''>-- Sin recargo de color --</option>
            {config.cromatismo.map(c => <option key={c.id} value={c.id}>{c.nombre} (+{c.recargoPct}%)</option>)}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Recargo edición <InfoTip text={TIPS.recargoEd} /></label>
          <select value={form.recargoEdicionId||''} onChange={e => setField('recargoEdicionId', e.target.value)}>
            <option value=''>-- Sin recargo de edición --</option>
            {config.recargosEd.map(r => {
              const esImpreso = r.nombre?.toLowerCase().includes('dominical') || r.nombre?.toLowerCase().includes('lanzamiento');
              return (
                <option key={r.id} value={r.id} disabled={esImpreso}>
                  {esImpreso ? '🔒 ' : ''}{r.nombre} (+{r.recargoPct}%){esImpreso ? ' — próximamente' : ''}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* 3. Vigencia */}
      <div style={SECCION_STYLE}>3. Vigencia</div>
      <div className={styles.form} style={GRID_3COLS}>
        <div className={styles.formGroup}>
          <label>Modalidad de vigencia <InfoTip text={TIPS.modalidad} /></label>
          <select value={form.modalidadVigencia} onChange={e => { setField('modalidadVigencia', e.target.value); setResult(null); }}>
            <option value='ediciones'>Por número de ediciones</option>
            <option value='fechas'>Por rango de fechas</option>
            <option value='indefinida'>Indefinida (precio por inserción)</option>
          </select>
        </div>

        {form.modalidadVigencia === 'ediciones' && (
          <div className={styles.formGroup}>
            <label>Número de ediciones <InfoTip text={TIPS.numEdiciones} /></label>
            <input type='number' min='1' value={form.numEdiciones} onChange={e => setField('numEdiciones', e.target.value)} />
          </div>
        )}

        {form.modalidadVigencia === 'fechas' && (
          <>
            <div className={styles.formGroup}>
              <label>Fecha inicio <InfoTip text={TIPS.fechaInicio} /></label>
              <input type='date' value={form.fechaInicio||''} onChange={e => setField('fechaInicio', e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>Fecha fin <InfoTip text={TIPS.fechaFin} /></label>
              <input type='date' value={form.fechaFin||''} onChange={e => setField('fechaFin', e.target.value)} />
            </div>
          </>
        )}
      </div>

      {/* 4. Descuento por agencia (opcional) */}
      <div style={SECCION_STYLE}>4. Descuento por agencia (opcional)</div>
      <div className={styles.form} style={GRID_3COLS}>
        <div className={styles.formGroup} style={{ gridColumn: form.esAgencia ? 'auto' : '1 / -1' }}>
          <label style={{ textTransform:'none', letterSpacing:0, fontWeight:500, display:'flex', alignItems:'center', gap:8 }}>
            <input type='checkbox' checked={form.esAgencia} onChange={e => setField('esAgencia', e.target.checked)} />
            Es agencia de medios
            <InfoTip text={TIPS.agencia} />
          </label>
        </div>
        {form.esAgencia && (
          <div className={styles.formGroup}>
            <label>% descuento especial <InfoTip text={TIPS.descuentoEsp} /></label>
            <input type='number' placeholder='Vacío = descuento estándar' value={form.descuentoEspecialPct||''} onChange={e => setField('descuentoEspecialPct', e.target.value)} />
          </div>
        )}
      </div>

      {/* Acción */}
      <div className={styles.formActions} style={{ marginTop:8 }}>
        {error && <span style={{ color:'#dc2626', fontSize:13, marginRight:12 }}>{error}</span>}
        <button className={styles.btnPrimary} onClick={cotizar} disabled={loading}>
          <Calculator size={14} />
          {loading ? 'Calculando...' : 'Calcular precio'}
        </button>
      </div>

      {result && (
        <div className={styles.cotizadorResult}>
          <div style={{ fontSize:13, color:'#555', marginBottom:4 }}>Precio por inserción</div>
          <div className={styles.cotizadorTotal}>{fmt(result.total)}</div>
          <div style={{ color:'#555', fontSize:12, marginBottom:8 }}>IVA incluido</div>

          <div style={{ background:'rgba(201,162,39,0.12)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13 }}>
            {form.modalidadVigencia === 'ediciones' && (
              <>
                <strong>{form.numEdiciones} edición{form.numEdiciones > 1 ? 'es' : ''}</strong>
                {' × '}{fmt(result.total)} = {' '}
                <strong style={{ fontSize:16, color:'#1B4332' }}>{fmt(result.totalConVigencia)}</strong>
                <span style={{ color:'#666', marginLeft:6 }}>total IVA incl.</span>
              </>
            )}
            {form.modalidadVigencia === 'fechas' && form.fechaInicio && form.fechaFin && (
              <>
                <strong>{result.insercionesTotales} días</strong>
                {' ('}{form.fechaInicio} → {form.fechaFin}{') × '}{fmt(result.total)} = {' '}
                <strong style={{ fontSize:16, color:'#1B4332' }}>{fmt(result.totalConVigencia)}</strong>
                <span style={{ color:'#666', marginLeft:6 }}>total IVA incl.</span>
              </>
            )}
            {form.modalidadVigencia === 'indefinida' && (
              <span style={{ color:'#666' }}>Precio por inserción — vigencia indefinida</span>
            )}
          </div>

          <div style={{ borderTop:'1px solid #a7f3d0', paddingTop:12 }}>
            {[
              ['Precio base (tarifa × unidades)', result.precioBase],
              ['+ Recargo ubicación',             result.recargoUbicacion],
              ['+ Recargo cromatismo',            result.recargoCromatismo],
              ['+ Recargo edición',               result.recargoEdicion],
              ['= Subtotal',                      result.subtotal],
              ['- Descuento agencia',             result.descuentoAgencia],
              ['= Base para IVA',                 result.baseIva],
              ['+ IVA (19%)',                     result.iva],
              ['TOTAL',                           result.total],
            ].map(([label, val]) => (
              <div key={label} className={styles.cotizadorRow}>
                <span>{label}</span>
                <span style={{ fontWeight: label === 'TOTAL' ? 700 : 400 }}>{fmt(val)}</span>
              </div>
            ))}
          </div>

          <div className={styles.cotizadorDetalle}>{result.detalle}</div>
        </div>
      )}
    </div>
  );
}
